import { createTheme, alpha } from "@mui/material";

/** Brand accent carried over from the original reader. */
export const ACCENT = "#E97B34";
export const ACCENT_HOVER = "#F08F4E";

export const surface = {
  page: "#141414",
  card: "#1E1E1E",
  cardHover: "#252525",
  raised: "#2A2A2A",
  border: "#333333",
  borderStrong: "#454545",
};

/**
 * Text tokens. The original UI used #666 for metadata, which fails WCAG AA on
 * these backgrounds; secondary/tertiary here clear 4.5:1 and 3:1 respectively.
 */
export const text = {
  primary: "#F5F5F5",
  secondary: "#B4B4B4",
  tertiary: "#8E8E8E",
};

export const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: ACCENT, contrastText: "#1A1008" },
    background: { default: surface.page, paper: surface.card },
    error: { main: "#FF6B6B" },
    success: { main: "#4CC38A" },
    text: { primary: text.primary, secondary: text.secondary },
    divider: surface.border,
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: { fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.02em" },
    h2: { fontSize: "0.8125rem", fontWeight: 700, letterSpacing: "0.08em" },
    h3: { fontSize: "1rem", fontWeight: 650, lineHeight: 1.35 },
    body2: { fontSize: "0.875rem", lineHeight: 1.55 },
    caption: { fontSize: "0.75rem", lineHeight: 1.4 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Keyboard focus was invisible in the original build.
        "*:focus-visible": {
          outline: `2px solid ${ACCENT}`,
          outlineOffset: 2,
          borderRadius: 4,
        },
        "@media (prefers-reduced-motion: reduce)": {
          "*": {
            animationDuration: "0.01ms !important",
            transitionDuration: "0.01ms !important",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: surface.card,
          backgroundImage: "none",
          border: `1px solid ${surface.border}`,
          borderRadius: 14,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { borderRadius: 999 } },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 550 },
        outlined: { borderColor: surface.borderStrong },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: surface.raised,
          "& fieldset": { borderColor: surface.border },
          "&:hover fieldset": { borderColor: surface.borderStrong },
          "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: 1 },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: surface.card,
          backgroundImage: "none",
          border: `1px solid ${surface.border}`,
          borderRadius: 16,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#000",
          border: `1px solid ${surface.border}`,
          fontSize: "0.75rem",
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": { color: ACCENT },
          "&.Mui-checked + .MuiSwitch-track": {
            backgroundColor: ACCENT,
            opacity: 0.5,
          },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: alpha("#FFFFFF", 0.06) },
      },
    },
  },
});
